import { FunctionComponent } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Stage } from '@/model';

const PREFIX = 'pages.application_championship_stage_standings.legend';

type LegendDotProps = { color: string };
const LegendDot: FunctionComponent<LegendDotProps> = ({ color }) => (
  <Box w="10px" h="10px" borderRadius="full" bg={color} flexShrink={0} />
);

type StandingsLegendProps = {
  stage?: Stage;
};

/**
 * Legenda das zonas de classificação. Quando a stage tem `*_count` configurados,
 * mostra "Label (n)" — caso contrário, label puro (decorativo).
 */
export const StandingsLegend: FunctionComponent<StandingsLegendProps> = ({ stage }) => {
  const { t } = useTranslation();

  const direct = stage?.direct_qualifiers_count ?? 0;
  const playoff = stage?.playoff_qualifiers_count ?? 0;
  const secondary = stage?.secondary_qualifiers_count ?? 0;
  const relegated = stage?.relegated_count ?? 0;
  const formatLabel = (label: string, count: number) =>
    count > 0 ? `${label} (${count})` : label;

  return (
    <Flex
      align="center"
      gap={4}
      wrap="wrap"
      px={4}
      py={3}
      borderTopWidth="1px"
      borderColor="gray.100"
    >
      <Text fontSize="sm" fontWeight="medium" color="gray.700">
        {t(`${PREFIX}.title`)}
      </Text>

      <Flex align="center" gap={2}>
        <LegendDot color="green.500" />
        <Text fontSize="sm" color="gray.600">
          {formatLabel(t(`${PREFIX}.direct`), direct)}
        </Text>
      </Flex>

      <Flex align="center" gap={2}>
        <LegendDot color="blue.500" />
        <Text fontSize="sm" color="gray.600">
          {formatLabel(t(`${PREFIX}.playoff`), playoff)}
        </Text>
      </Flex>

      <Flex align="center" gap={2}>
        <LegendDot color="orange.500" />
        <Text fontSize="sm" color="gray.600">
          {formatLabel(t(`${PREFIX}.secondary`), secondary)}
        </Text>
      </Flex>

      <Flex align="center" gap={2}>
        <LegendDot color="red.500" />
        <Text fontSize="sm" color="gray.600">
          {formatLabel(t(`${PREFIX}.relegation`), relegated)}
        </Text>
      </Flex>

      <Text fontSize="xs" color="gray.500" ml="auto" fontStyle="italic">
        {t(`${PREFIX}.computed_hint`)}
      </Text>
    </Flex>
  );
};
